import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useGetTargetSchema } from '@/api/control/control-plane/control-plane';
import { isPayloadTooLarge } from '@/lib/apiErrors';

/**
 * Props for the TargetSchemaDialog component.
 */
interface TargetSchemaDialogProps {
  /** The organization ID. */
  orgId: string;
  /** The profile ID. */
  profileId: string;
  /** The source ID. */
  sourceId: string;
  /** The source table ID whose destination schema is shown (undefined when no row is selected). */
  sourceTableId?: string;
  /** The source table's logical name, shown in the dialog subtitle. */
  logicalName?: string;
  /** Whether the source table has completed its initial sync (target table exists). */
  hasInitialSyncCompleted?: boolean;
  /** Whether the dialog is open. */
  open: boolean;
  /** Handler for open-state changes. */
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog that shows a source table's live destination SQL schema (columns, SQL types, nullability,
 * and primary keys), read directly from the target database. When the target table maps to sidecar
 * tables, each is shown as its own grouped section. Before the first sync the target table does not
 * exist yet, so an informational empty state is shown instead of querying.
 */
export function TargetSchemaDialog({
  orgId,
  profileId,
  sourceId,
  sourceTableId,
  logicalName,
  hasInitialSyncCompleted,
  open,
  onOpenChange,
}: TargetSchemaDialogProps) {
  const { t } = useLanguage();
  const ts = t.profiles.targetSchemaDialog;

  // Only query once the dialog is open, a table is selected, and it has synced (so the table exists).
  const shouldFetch = open && !!sourceTableId && hasInitialSyncCompleted === true;

  const { data, isLoading, isError, error } = useGetTargetSchema(
    orgId,
    profileId,
    sourceId,
    sourceTableId ?? '',
    { query: { enabled: shouldFetch } }
  );

  const tables = data?.data.tables ?? [];
  const isEmpty = !isLoading && !isError && tables.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>{ts.title}</DialogTitle>
          <DialogDescription className="font-mono text-xs">{logicalName}</DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 space-y-4 pr-1">
          {hasInitialSyncCompleted !== true ? (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>{ts.availableAfterFirstSync}</AlertDescription>
            </Alert>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : isError ? (
            <Alert variant="destructive">
              <Info className="h-4 w-4" />
              <AlertDescription>{isPayloadTooLarge(error) ? ts.tooLargeError : ts.loadError}</AlertDescription>
            </Alert>
          ) : isEmpty ? (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>{ts.availableAfterFirstSync}</AlertDescription>
            </Alert>
          ) : (
            tables.map((table) => (
              <div key={table.targetTableName} className="space-y-2">
                {/* Only label each section when there is more than one table (sidecars present). */}
                {tables.length > 1 && (
                  <h4 className="text-sm font-semibold font-mono">{table.targetTableName}</h4>
                )}
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{ts.columnName}</TableHead>
                        <TableHead>{ts.sqlType}</TableHead>
                        <TableHead>{ts.nullable}</TableHead>
                        <TableHead>{ts.primaryKey}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(table.columns ?? []).map((col) => (
                        <TableRow key={col.column}>
                          <TableCell className="font-mono text-xs">{col.column}</TableCell>
                          <TableCell className="font-mono text-xs">{col.sqlType}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {col.isNullable ? t.profiles.sourceTables.yes : t.profiles.sourceTables.no}
                          </TableCell>
                          <TableCell>
                            {col.isPrimaryKey && (
                              <Badge variant="outline" className="text-xs">
                                {ts.primaryKeyBadge}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end shrink-0 pt-2">
          <Button variant="outline" onClick={() => { onOpenChange(false); }}>
            {ts.close}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
